import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "@webtemplate/ui";
import {
  trpc,
  setAccessToken,
  setRefreshHandler,
  clearRefreshHandler,
  TRPCClientError,
} from "@/core/api";
import type {
  AuthContextValue,
  AuthResponse,
  AuthState,
  AuthUser,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  LoginCredentials,
  LoginResult,
  RegisterCredentials,
  RegisterResponse,
  RegisterWithTenantCredentials,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SimpleResponse,
  TwoFactorBackupVerification,
  TwoFactorVerification,
} from "./types";

import type { UserStatus } from "@/core/types/api";

// AuthContext now lives in @webtemplate/ui (D-20); this module owns only the
// tRPC-backed provider. Re-exported so `@/core/auth` consumers keep resolving it.
export { AuthContext };

/** The shape the extended `me` query returns (08.7-03). */
interface MeResult {
  userId: string;
  tenantId?: string | null;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  status?: string | null;
}

/**
 * Build an AuthUser from the extended `me` response. The profile fields
 * (firstName/lastName/email/status) are best-effort on the server (they degrade
 * to null without 500-ing the session), so we coalesce nulls to a safe shape:
 * email falls back to "" and status to "ACTIVE" only as a last resort. Role
 * flags gate nav + admin routes and always come straight from the server.
 */
function toAuthUser(me: MeResult): AuthUser {
  return {
    id: me.userId,
    email: me.email ?? "",
    firstName: me.firstName ?? undefined,
    lastName: me.lastName ?? undefined,
    status: (me.status as UserStatus | null) ?? "ACTIVE",
    tenantId: me.tenantId ?? undefined,
    isAdmin: me.isAdmin ?? false,
    isSuperAdmin: me.isSuperAdmin ?? false,
  };
}

type AuthAction =
  | { type: "RESTORE_START" }
  | { type: "RESTORE_FAILURE" }
  | { type: "LOGIN_START" }
  | { type: "LOGIN_SUCCESS"; user: AuthUser; accessToken: string }
  | { type: "LOGIN_FAILURE" }
  | { type: "LOGOUT" };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "RESTORE_START":
      return { ...state, status: "loading" };
    case "RESTORE_FAILURE":
      return { user: null, status: "unauthenticated", accessToken: null };
    case "LOGIN_START":
      return { ...state, status: "loading" };
    case "LOGIN_SUCCESS":
      return {
        user: action.user,
        status: "authenticated",
        accessToken: action.accessToken,
      };
    case "LOGIN_FAILURE":
      return { user: null, status: "unauthenticated", accessToken: null };
    case "LOGOUT":
      return { user: null, status: "unauthenticated", accessToken: null };
  }
}

const initialState: AuthState = {
  user: null,
  status: "idle",
  accessToken: null,
};

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Extract a tRPC error message for user display.
 */
function getTrpcErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof TRPCClientError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

/**
 * Check if a tRPC error represents an EMAIL_NOT_VERIFIED response.
 * The backend maps EMAIL_NOT_VERIFIED to PRECONDITION_FAILED (412).
 * We check both the structured tRPC code and the HTTP status to ensure
 * this does not false-match other PRECONDITION_FAILED errors.
 */
export function isEmailNotVerifiedError(error: unknown): boolean {
  if (error instanceof TRPCClientError) {
    return error.data?.code === "PRECONDITION_FAILED" && error.data?.httpStatus === 412;
  }
  return false;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  const mountedRef = useRef(true);

  // Single-flight refresh: refresh tokens are single-use (rotated server-side),
  // so concurrent refresh calls race — the loser presents an already-consumed
  // token, fails, and logs the session out. Share ONE in-flight call between
  // the 401 interceptor and (StrictMode double-fired) restore effects.
  const refreshInFlightRef = useRef<Promise<string> | null>(null);
  const refreshAccessToken = useCallback((): Promise<string> => {
    refreshInFlightRef.current ??= utils.client.auth.refresh
      .mutate()
      .then((result) => {
        setAccessToken(result.accessToken);
        return result.accessToken;
      })
      .finally(() => {
        refreshInFlightRef.current = null;
      });
    return refreshInFlightRef.current;
  }, [utils]);

  // Register the refresh handler for the tRPC client auto-refresh interceptor.
  // This is called once on mount so the tRPC fetch wrapper can refresh tokens on 401.
  useEffect(() => {
    mountedRef.current = true;
    setRefreshHandler(async () => {
      try {
        return await refreshAccessToken();
      } catch {
        // Refresh failed -- user needs to re-authenticate
        if (mountedRef.current) {
          dispatch({ type: "LOGOUT" });
          setAccessToken(null);
        }
        return null;
      }
    });

    return () => {
      mountedRef.current = false;
      clearRefreshHandler();
    };
  }, [refreshAccessToken]);

  // Session restore: attempt to refresh token on mount using httpOnly refresh cookie.
  // Depend on `utils` (stable) rather than nested proxy properties.
  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      dispatch({ type: "RESTORE_START" });
      try {
        // Single-flight: StrictMode double-fires this effect; both runs must
        // share one refresh call or the duplicate consumes a rotated token.
        const accessToken = await refreshAccessToken();
        if (cancelled) return;

        // Fetch full user profile with the new access token. The extended `me`
        // (08.7-03) returns userId/tenantId/isAdmin/isSuperAdmin PLUS the profile
        // (firstName/lastName/email/status). Hydrate the full profile on restore
        // so the TopBar avatar derives role-correct initials CONSISTENTLY on every
        // route and for every role — not just on the dashboard after a fresh
        // login. The previous placeholder (email:"") collapsed the avatar to "?"
        // on any non-login route and for tenant users (08.7-09 sign-off fix).
        const meResult = await utils.client.auth.me.query();
        if (cancelled) return;

        dispatch({
          type: "LOGIN_SUCCESS",
          user: toAuthUser(meResult),
          accessToken,
        });
      } catch {
        if (cancelled) return;
        // No valid refresh token or refresh not implemented yet -- unauthenticated
        setAccessToken(null);
        dispatch({ type: "RESTORE_FAILURE" });
      }
    }

    void restoreSession();

    return () => {
      cancelled = true;
    };
  }, [utils, refreshAccessToken]);

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<LoginResult> => {
      dispatch({ type: "LOGIN_START" });
      try {
        const result = await utils.client.auth.login.mutate({
          email: credentials.email,
          password: credentials.password,
          rememberMe: credentials.rememberMe,
        });

        if (result.requiresTwoFactor) {
          // 2FA required -- return session token, stay in loading/unauthenticated
          dispatch({ type: "LOGIN_FAILURE" });
          return {
            requiresTwoFactor: true,
            sessionToken: result.sessionToken,
          };
        }

        // Login success
        setAccessToken(result.accessToken);
        dispatch({
          type: "LOGIN_SUCCESS",
          user: result.user as AuthUser,
          accessToken: result.accessToken,
        });
        return {
          accessToken: result.accessToken,
          user: result.user as AuthUser,
        };
      } catch (error) {
        dispatch({ type: "LOGIN_FAILURE" });
        throw new Error(getTrpcErrorMessage(error, "Login failed"), { cause: error });
      }
    },
    [utils],
  );

  const register = useCallback(
    async (credentials: RegisterCredentials): Promise<RegisterResponse> => {
      try {
        const result = await utils.client.auth.register.mutate({
          email: credentials.email,
          password: credentials.password,
          confirmPassword: credentials.confirmPassword,
          firstName: credentials.firstName,
          lastName: credentials.lastName,
        });

        // Registration does NOT authenticate the user -- email verification required.
        // Return a dedicated RegisterResponse (not AuthResponse) to avoid callers
        // treating this as a valid auth session.
        return {
          success: result.success,
          message: result.message,
        } satisfies RegisterResponse;
      } catch (error) {
        throw new Error(getTrpcErrorMessage(error, "Registration failed"), { cause: error });
      }
    },
    [utils],
  );

  const registerWithTenant = useCallback(
    async (credentials: RegisterWithTenantCredentials): Promise<RegisterResponse> => {
      try {
        const result = await utils.client.auth.registerWithTenant.mutate({
          email: credentials.email,
          password: credentials.password,
          confirmPassword: credentials.confirmPassword,
          // Thread the REAL collected names + org/subdomain (no hardcoded values).
          firstName: credentials.firstName,
          lastName: credentials.lastName,
          orgName: credentials.orgName,
          subdomain: credentials.subdomain,
          acceptTerms: credentials.acceptTerms,
        });

        // Self-service signup does NOT authenticate -- email verification required.
        return {
          success: result.success,
          message: result.message,
        } satisfies RegisterResponse;
      } catch (error) {
        throw new Error(getTrpcErrorMessage(error, "Registration failed"), { cause: error });
      }
    },
    [utils],
  );

  const verifyTwoFactor = useCallback(
    async (verification: TwoFactorVerification): Promise<AuthResponse> => {
      dispatch({ type: "LOGIN_START" });
      try {
        const result = await utils.client.auth.verifyTwoFactor.mutate({
          code: verification.code,
          sessionToken: verification.sessionToken,
        });

        setAccessToken(result.accessToken);
        dispatch({
          type: "LOGIN_SUCCESS",
          user: result.user as AuthUser,
          accessToken: result.accessToken,
        });
        return {
          accessToken: result.accessToken,
          user: result.user as AuthUser,
        };
      } catch (error) {
        dispatch({ type: "LOGIN_FAILURE" });
        throw new Error(getTrpcErrorMessage(error, "Two-factor verification failed"), {
          cause: error,
        });
      }
    },
    [utils],
  );

  const verifyBackupCode = useCallback(
    async (verification: TwoFactorBackupVerification): Promise<AuthResponse> => {
      dispatch({ type: "LOGIN_START" });
      try {
        const result = await utils.client.auth.verifyBackupCode.mutate({
          backupCode: verification.backupCode,
          sessionToken: verification.sessionToken,
        });

        setAccessToken(result.accessToken);
        dispatch({
          type: "LOGIN_SUCCESS",
          user: result.user as AuthUser,
          accessToken: result.accessToken,
        });
        return {
          accessToken: result.accessToken,
          user: result.user as AuthUser,
        };
      } catch (error) {
        dispatch({ type: "LOGIN_FAILURE" });
        throw new Error(getTrpcErrorMessage(error, "Backup code verification failed"), {
          cause: error,
        });
      }
    },
    [utils],
  );

  const verifyEmailOtp = useCallback(
    async (verification: TwoFactorVerification): Promise<AuthResponse> => {
      dispatch({ type: "LOGIN_START" });
      try {
        const result = await utils.client.auth.verifyEmailOtp.mutate({
          code: verification.code,
          sessionToken: verification.sessionToken,
        });

        setAccessToken(result.accessToken);
        dispatch({
          type: "LOGIN_SUCCESS",
          user: result.user as AuthUser,
          accessToken: result.accessToken,
        });
        return {
          accessToken: result.accessToken,
          user: result.user as AuthUser,
        };
      } catch (error) {
        dispatch({ type: "LOGIN_FAILURE" });
        throw new Error(getTrpcErrorMessage(error, "Email OTP verification failed"), {
          cause: error,
        });
      }
    },
    [utils],
  );

  const requestEmailOtp = useCallback(
    async (sessionToken: string): Promise<SimpleResponse> => {
      try {
        const result = await utils.client.auth.requestEmailOtp.mutate({
          sessionToken,
        });
        return {
          success: result.success,
          message: result.message,
        };
      } catch (error) {
        throw new Error(getTrpcErrorMessage(error, "Failed to send email code"), { cause: error });
      }
    },
    [utils],
  );

  const requestMagicLink = useCallback(
    async (email: string): Promise<SimpleResponse> => {
      try {
        const result = await utils.client.auth.requestMagicLink.mutate({
          email,
        });
        return {
          success: result.success,
          message: result.message,
        };
      } catch (error) {
        throw new Error(getTrpcErrorMessage(error, "Failed to send magic link"), { cause: error });
      }
    },
    [utils],
  );

  const forgotPassword = useCallback(
    async (request: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
      try {
        const result = await utils.client.auth.forgotPassword.mutate({
          email: request.email,
        });
        // CR-01: the endpoint returns a byte-identical generic acknowledgement
        // for every input — no 2FA/existence fields to propagate.
        return {
          success: result.success,
          message: result.message,
        };
      } catch (error) {
        throw new Error(getTrpcErrorMessage(error, "Password reset request failed"), {
          cause: error,
        });
      }
    },
    [utils],
  );

  const resetPassword = useCallback(
    async (request: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
      try {
        const result = await utils.client.auth.resetPassword.mutate({
          token: request.token,
          password: request.password,
          confirmPassword: request.confirmPassword,
          twoFactorMethod: request.twoFactorMethod,
          twoFactorCode: request.twoFactorCode,
        });

        // CR-01: a 2FA account must supply a code at the reset step. The server
        // signals this without consuming the token, so the user can resubmit.
        if (result.requiresTwoFactor) {
          return {
            success: false,
            message: "Two-factor verification required",
            requiresTwoFactor: true,
          };
        }

        // resetPassword returns accessToken + user (auto-login after reset)
        if (result.accessToken) {
          setAccessToken(result.accessToken);
          dispatch({
            type: "LOGIN_SUCCESS",
            user: result.user as AuthUser,
            accessToken: result.accessToken,
          });
        }

        return { success: true, message: "Password reset successfully" };
      } catch (error) {
        throw new Error(getTrpcErrorMessage(error, "Password reset failed"), { cause: error });
      }
    },
    [utils],
  );

  const logout = useCallback(async () => {
    try {
      await utils.client.auth.logout.mutate();
    } catch {
      // Best-effort server-side logout; clear local state regardless
    }
    setAccessToken(null);
    dispatch({ type: "LOGOUT" });
    // Clear the entire React Query cache (not just invalidate). queryClient.clear()
    // removes all cached data immediately, preventing stale protected query results
    // from being visible after logout or when another user signs in.
    // utils.invalidate() only marks queries as stale and triggers refetches, which
    // can leave data visible until the refetch completes.
    queryClient.clear();
  }, [utils, queryClient]);

  // Role flags come straight from the server (me/login), computed via the same
  // RoleService the API enforces with. Deriving from a client-side role string
  // list was the bug that made isAdmin/isSuperAdmin permanently false.
  const isAdmin = useMemo(() => state.user?.isAdmin ?? false, [state.user]);

  const isSuperAdmin = useMemo(() => state.user?.isSuperAdmin ?? false, [state.user]);

  const contextValue: AuthContextValue = useMemo(
    () => ({
      user: state.user,
      status: state.status,
      isAuthenticated: state.status === "authenticated",
      isAdmin,
      isSuperAdmin,
      login,
      register,
      registerWithTenant,
      verifyTwoFactor,
      verifyBackupCode,
      verifyEmailOtp,
      requestEmailOtp,
      requestMagicLink,
      forgotPassword,
      resetPassword,
      logout,
    }),
    [
      state.user,
      state.status,
      isAdmin,
      isSuperAdmin,
      login,
      register,
      registerWithTenant,
      verifyTwoFactor,
      verifyBackupCode,
      verifyEmailOtp,
      requestEmailOtp,
      requestMagicLink,
      forgotPassword,
      resetPassword,
      logout,
    ],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  return getTrpcErrorMessage(error, fallback);
}
